import * as React from 'react';
import { TextField, TextFieldProps, Tooltip } from '@mui/material';
import { roundNumber } from '../../utils/common';

/**
 * Props for {@link MNumber}.
 */
interface MNumberProps extends Omit<TextFieldProps, 'onChange' | 'defaultValue' | 'value'> {
    /** Total digits after the decimal point, e.g. `10.05 => .05` is 2 digits. */
    decimal?: number | null;
    /** Allowed `[min, max]` input range, e.g. min `0` -> max `5`. */
    range?: [number | undefined, number | undefined] | null;
    /** When `true`, allows free-form text input such as a phone number: `+1 (770) 323-1122`. Defaults to `false`. */
    isTextNumber?: boolean;
    /** When `true`, mouse-wheel scrolling changes the value instead of blurring the field. Defaults to `false`. */
    isOnWheel?: boolean;
    /** Whether negative values are allowed. Defaults to `true`. */
    isNegative?: boolean;
    /**
     * Called with the new (possibly rounded/clamped) value.
     *
     * @param value - The current numeric (or text-number) value.
     */
    onChange: (value: number | string) => void;
    /** Initial value applied whenever it changes. */
    defaultValue?: number | string;
    /** Controlled value. */
    value?: number | string;
    /** Tooltip shown when hovering the field. */
    tooltip?: string;
    /** Adornment rendered at the start of the input. */
    startAdornment?: React.ReactNode;
    min?: number;
    max?: number;
}

/**
 * A numeric MUI {@link TextField} supporting decimal rounding, min/max
 * clamping, negative-value blocking, and an optional "text number" mode for
 * phone-number-like free-form input.
 */
const MNumber: React.FC<MNumberProps> = ({
    decimal = null,
    range = null,
    isTextNumber = false,
    isOnWheel = false, // scroll to change value
    isNegative = true,
    onChange, defaultValue, value, tooltip, startAdornment, ...props
}) => {
    React.useEffect(() => {
        if (defaultValue !== undefined) {
            setDefaultNumValue(defaultValue);
            setNumValue(defaultValue);
        }
    }, [defaultValue]);

    React.useEffect(() => {
        if (value !== undefined) setNumValue(value);
    }, [value]);

    const [focused, setFocused] = React.useState(false);
    const [numValue, setNumValue] = React.useState<number | string>('');
    const [defaultNumValue, setDefaultNumValue] = React.useState<number | string | undefined>();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        let _newValue: number | string = value;
        if (!isTextNumber) _newValue = roundNumber(value, decimal) as number;
        if (value !== '') {
            if (typeof range?.[0] === 'number') {
                if ((_newValue as number) < range?.[0]) _newValue = range[0];
            }
            if (typeof range?.[1] === 'number') {
                if ((_newValue as number) > range?.[1]) _newValue = range[1];
            }
        }
        if (!isNegative && (_newValue as number) < 0) _newValue = 0;
        setNumValue(_newValue);
        onChange(_newValue);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        let isPrevent = false;
        if (isTextNumber) {
            const phonePattern = /^[\d()+-\s]+$/;
            if (!phonePattern.test(event.key)) isPrevent = true;
        } else {
            // prevent dot character from being entered
            if (!decimal && event.key === '.') isPrevent = true;
            if (event.key === 'e') isPrevent = true;
            if (decimal && decimal > 0) {
                const countDecimal = (event.target as HTMLInputElement).value.split('.')?.[1];
                if (countDecimal?.length >= decimal) {
                    isPrevent = true;
                    // Text was selection
                    const selection = window.getSelection();
                    if (selection?.type === 'Range' && selection.toString() === value) isPrevent = false;
                }
            }
        }
        if (isPrevent) event.preventDefault();
    };

    const handleFocus = () => {
        setFocused(true);
    };

    const handleBlur = () => {
        setFocused(false);
    };

    return (
        <Tooltip title={tooltip ?? ''}>
            <TextField {...props}
                fullWidth
                defaultValue={defaultNumValue}
                value={numValue}
                onChange={handleChange}
                slotProps={{
                    inputLabel: {
                        shrink: focused || numValue !== '' || defaultNumValue !== undefined
                    },
                    input: {
                        startAdornment: startAdornment || undefined
                    },
                    htmlInput: {
                        type: isTextNumber ? 'text' : 'number',
                        autoComplete: 'on',
                        onKeyPress: handleKeyPress,
                        min: range?.[0],
                        max: range?.[1],
                        onFocus: handleFocus,
                        onBlur: handleBlur
                    }
                }}
                onWheel={(e) => isOnWheel ? e : (e.target as HTMLInputElement).blur()}
            />
        </Tooltip>
    );
};

export default React.memo(MNumber);
